import { useState, useEffect, useCallback } from "react";

// Decodes the raw file into a bitmap with EXIF orientation baked into real
// pixels — this must happen BEFORE any preview/crop/export, otherwise a photo
// taken sideways/upside-down on a phone stays visually wrong no matter what
// the backend does afterwards (its own re-encode strips the orientation tag).
async function loadOrientedImage(file) {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0);
    return canvas.toDataURL("image/jpeg", 0.95);
  } catch {
    // Older engines without createImageBitmap's imageOrientation option —
    // <img> + canvas drawImage already respects EXIF orientation natively
    // on every current mainstream browser.
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
}

async function exportCroppedSquare(imageSrc, cropAreaPixels, outputSize = 512) {
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    img,
    cropAreaPixels.x, cropAreaPixels.y, cropAreaPixels.width, cropAreaPixels.height,
    0, 0, outputSize, outputSize
  );
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
}

// Drives the "reposition + zoom + crop 1:1" step of the avatar editor — rendered
// inline inside the existing profile-avatar EditSectionDrawer (not a separate
// overlay), so it shares the drawer's own Guardar/Cancelar buttons.
export function useAvatarCropper(file) {
  const [imageSrc, setImageSrc] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropAreaPixels, setCropAreaPixels] = useState(null);

  useEffect(() => {
    setImageSrc(null); setLoadError(false); setCrop({ x: 0, y: 0 }); setZoom(1); setCropAreaPixels(null);
    if (!file) return;
    let active = true;
    loadOrientedImage(file).then((src) => { if (active) setImageSrc(src); }).catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, [file]);

  const onCropComplete = useCallback((_area, areaPixels) => setCropAreaPixels(areaPixels), []);

  const confirmCrop = useCallback(async () => {
    if (!imageSrc || !cropAreaPixels) return null;
    return exportCroppedSquare(imageSrc, cropAreaPixels);
  }, [imageSrc, cropAreaPixels]);

  return { imageSrc, loadError, crop, setCrop, zoom, setZoom, onCropComplete, confirmCrop, ready: !!cropAreaPixels };
}
